import {
  FREE_COURT_SLUG,
  INDOOR_COURT_SLUG,
  OUTDOOR_COURT_SLUG,
  ROOT_PAGE,
  getCityAllCourtTagVenueUrlWithSport,
  getCityCourtsPageUrlWithSport,
  getCountryIndexPageUrlWithSport,
  getCountrySubdivisionIndexPageUrlWithSport,
} from 'constants/pages';
import { VenueAccessTypesEnum } from 'types/generated/client';
import type { GetVenuesByGeoQuery } from 'types/generated/server';
import Home from 'svg/Home';
import { Props } from './types';

const US_SLUG = 'us';
export const getTitleLocationName = (city: Props['city']) => {
  if (city.countrySubdivision.country.slug === US_SLUG && city.countrySubdivision.name) {
    return `${city.name}, ${city.countrySubdivision.name}`;
  } else {
    return city.name;
  }
};

/**
 * SEO body copy rendered under the page title on the city courts pages.
 * Intentionally descriptive for search/AI crawlers while staying subtle in the UI.
 */
export const getCourtsSeoDescription = ({
  city,
  sport,
  tagSlug,
}: {
  city: Props['city'];
  sport?: string;
  tagSlug?: string;
}) => {
  const sportNameLower = sport ? sport.toLowerCase() : 'pickleball';
  const location = getTitleLocationName(city);

  switch (tagSlug) {
    case FREE_COURT_SLUG:
      return `Find free ${sportNameLower} courts in ${location}. Explore public courts, recreation centers, and affordable places to play near you.`;
    case INDOOR_COURT_SLUG:
      return `Discover indoor ${sportNameLower} courts in ${location}. Browse indoor facilities and year-round places to play, including lessons, leagues, and local events near you.`;
    case OUTDOOR_COURT_SLUG:
      return `Explore outdoor ${sportNameLower} courts in ${location}. Find parks, outdoor facilities, and local places to play near you.`;
    default: {
      const courtCount = city?.venues?.length || 0;
      const countClause =
        courtCount > 0
          ? `Explore ${courtCount} ${courtCount === 1 ? 'court' : 'courts'}, discover`
          : 'Discover';
      return `Find the best places to play ${sportNameLower} in ${location}. ${countClause} indoor and outdoor play, and connect with local lessons, leagues, and events near you.`;
    }
  }
};

export const generateCourtsMeta = ({ city, sport }: { city: Props['city']; sport: string }) => {
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';
  const sportNameLower = sport ? sport.toLowerCase() : 'pickleball';
  const metaTitle = `${sportName} Courts in ${getTitleLocationName(city)} (${
    city?.venues?.length
  } locations)`;
  const metaDescription = `Find ${sportNameLower} courts near you in ${getTitleLocationName(
    city,
  )}. Play ${sportNameLower} at ${city?.venues?.length} courts.`;

  return {
    metaTitle,
    metaDescription,
  };
};
export const generateCourtsTagMeta = ({
  city,
  tagName,
  sport,
}: {
  city: Props['city'];
  tagName: string;
  sport: string;
}) => {
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';
  const sportNameLower = sport ? sport.toLowerCase() : 'pickleball';
  const metaTitle = `${tagName} ${sportName} Courts in ${getTitleLocationName(city)} (${
    city?.venues?.length
  } locations)`;
  const metaDescription = `Find ${tagName.toLowerCase()} ${sportNameLower} courts near you in ${getTitleLocationName(
    city,
  )}. Play ${sportNameLower} at ${city?.venues?.length} ${tagName.toLowerCase()} courts.`;

  return {
    metaTitle,
    metaDescription,
  };
};

const getSharedBreadcrumbsUi = ({
  cityName,
  citySlug,
  regionName,
  regionSlug,
  countryId,
  countryName,
  countrySlug,
  sport,
}: {
  cityName: string;
  citySlug: string;
  regionName: string;
  regionSlug: string;
  countryId: string;
  countryName: string;
  countrySlug: string;
  sport: string;
}) => {
  return [
    {
      label: <Home className="w-4" />,
      url: ROOT_PAGE,
    },
    {
      label: countryId || '',
      url: getCountryIndexPageUrlWithSport(countrySlug || '', sport),
    },
    {
      label: regionName || '',
      url: getCountrySubdivisionIndexPageUrlWithSport(regionSlug || '', sport),
    },
    {
      label: cityName || '',
      url: getCityCourtsPageUrlWithSport(citySlug || '', sport),
    },
  ];
};
const getSharedBreadcrumbsJsonLd = ({
  cityName,
  citySlug,
  regionName,
  regionSlug,
  countryId,
  countryName,
  countrySlug,
  sport,
}: {
  cityName: string;
  citySlug: string;
  regionName: string;
  regionSlug: string;
  countryId: string;
  countryName: string;
  countrySlug: string;
  sport: string;
}) => {
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';

  return [
    {
      name: 'Home',
      id: ROOT_PAGE,
    },
    {
      name: `${countryName || ''} ${sportName}`,
      id: getCountryIndexPageUrlWithSport(countrySlug || '', sport),
    },
    {
      name: `${regionName || ''} ${sportName}`,
      id: getCountrySubdivisionIndexPageUrlWithSport(regionSlug || '', sport),
    },
    {
      name: `${cityName} ${sportName}`,
      id: getCityCourtsPageUrlWithSport(citySlug || '', sport),
    },
  ];
};

export const generateCourtsBreadcrumbs = ({
  isForJsonLd,
  cityName,
  citySlug,
  regionName,
  regionSlug,
  countryId,
  countryName,
  countrySlug,
  sport,
}: {
  isForJsonLd?: boolean;
  cityName: string;
  citySlug: string;
  regionName: string;
  regionSlug: string;
  countryId: string;
  countryName: string;
  countrySlug: string;
  sport: string;
}) => {
  const sharedUi = getSharedBreadcrumbsUi({
    cityName,
    citySlug,
    regionName,
    regionSlug,
    countryId,
    countryName,
    countrySlug,
    sport,
  });
  return {
    ui: isForJsonLd
      ? []
      : [
          ...sharedUi.slice(0, -1),
          {
            ...sharedUi[sharedUi.length - 1],
            url: '',
            isActivePage: true,
          },
        ],
    jsonLd: [
      ...getSharedBreadcrumbsJsonLd({
        cityName,
        citySlug,
        regionName,
        regionSlug,
        countryId,
        countryName,
        countrySlug,
        sport,
      }),
    ].map((item) => ({
      ...item,
      id: `${process.env.APP_URL}${item.id}`,
    })),
  };
};

export const generateCourtsTagBreadcrumbs = ({
  isForJsonLd,
  cityName,
  citySlug,
  regionName,
  regionSlug,
  countryId,
  countryName,
  countrySlug,
  tagName,
  tagSlug,
  sport,
}: {
  isForJsonLd?: boolean;
  cityName: string;
  citySlug: string;
  regionName: string;
  regionSlug: string;
  countryId: string;
  countryName: string;
  countrySlug: string;
  tagName: string;
  tagSlug: string;
  sport: string;
}) => {
  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Pickleball';

  return {
    ui: isForJsonLd
      ? []
      : [
          ...getSharedBreadcrumbsUi({
            cityName,
            citySlug,
            regionName,
            regionSlug,
            countryId,
            countryName,
            countrySlug,
            sport,
          }),
          {
            label: tagName,
            url: '',
            isActivePage: true,
          },
        ],
    jsonLd: [
      ...getSharedBreadcrumbsJsonLd({
        cityName,
        citySlug,
        regionName,
        regionSlug,
        countryId,
        countryName,
        countrySlug,
        sport,
      }),
      {
        name: `${tagName} ${cityName} ${sportName} Courts`,
        id: getCityAllCourtTagVenueUrlWithSport(citySlug || '', tagSlug, sport),
      },
    ].map((item) => ({
      ...item,
      id: `${process.env.APP_URL}${item.id}`,
    })),
  };
};

export const filterVenuesForTagFree = (venues: Props['city']['venues']) => {
  return venues.filter((venue, i) => venue.accessType === VenueAccessTypesEnum.Free);
};

export const filterNearbyVenuesForTagFree = (venues: GetVenuesByGeoQuery['venues']) => {
  return venues.filter((venue, i) => venue.accessType === VenueAccessTypesEnum.Free);
};

export const filterVenuesForTagOutdoor = (venues: Props['city']['venues']) => {
  return venues.filter((venue, i) => venue.outdoorCourtCount > 0);
};

export const filterNearbyVenuesForTagOutdoor = (venues: GetVenuesByGeoQuery['venues']) => {
  return venues.filter((venue, i) => venue.outdoorCourtCount > 0);
};

export const filterVenuesForTagIndoor = (venues: Props['city']['venues']) => {
  return venues.filter((venue, i) => venue.indoorCourtCount > 0);
};

export const filterNearbyVenuesForTagIndoor = (venues: GetVenuesByGeoQuery['venues']) => {
  return venues.filter((venue, i) => venue.indoorCourtCount > 0);
};

export const generateLessonsBreadcrumbs = ({
  isForJsonLd,
  cityName,
  citySlug,
  regionName,
  regionSlug,
  countryId,
  countryName,
  countrySlug,
  sport,
}: {
  isForJsonLd?: boolean;
  cityName: string;
  citySlug: string;
  regionName: string;
  regionSlug: string;
  countryId: string;
  countryName: string;
  countrySlug: string;
  sport?: string;
}) => {
  return {
    ui: isForJsonLd
      ? []
      : [
          ...getSharedBreadcrumbsUi({
            cityName,
            citySlug,
            regionName,
            regionSlug,
            countryId,
            countryName,
            countrySlug,
            sport: sport || '',
          }),
          {
            label: 'Lessons',
            url: '',
            isActivePage: true,
          },
        ],
    jsonLd: [
      ...getSharedBreadcrumbsJsonLd({
        cityName,
        citySlug,
        regionName,
        regionSlug,
        countryId,
        countryName,
        countrySlug,
        sport: sport || '',
      }),
    ].map((item) => ({
      ...item,
      id: `${process.env.APP_URL}${item.id}`,
    })),
  };
};

export const generateClinicsBreadcrumbs = ({
  isForJsonLd,
  cityName,
  citySlug,
  regionName,
  regionSlug,
  countryId,
  countryName,
  countrySlug,
  sport,
}: {
  isForJsonLd?: boolean;
  cityName: string;
  citySlug: string;
  regionName: string;
  regionSlug: string;
  countryId: string;
  countryName: string;
  countrySlug: string;
  sport?: string;
}) => {
  return {
    ui: isForJsonLd
      ? []
      : [
          ...getSharedBreadcrumbsUi({
            cityName,
            citySlug,
            regionName,
            regionSlug,
            countryId,
            countryName,
            countrySlug,
            sport: sport || '',
          }),
          {
            label: 'Clinics',
            url: '',
            isActivePage: true,
          },
        ],
    jsonLd: [
      ...getSharedBreadcrumbsJsonLd({
        cityName,
        citySlug,
        regionName,
        regionSlug,
        countryId,
        countryName,
        countrySlug,
        sport: sport || '',
      }),
    ].map((item) => ({
      ...item,
      id: `${process.env.APP_URL}${item.id}`,
    })),
  };
};
